import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "https://buydubai.ai";
const DEFAULT_DESC =
  "BuyDubai.ai is the AI platform for Dubai. Instead of searching many websites, tell BuyDubai.ai what you need — it discovers, compares and helps you take action.";

// Route → { title, desc }
const META = {
  "/": { title: "BuyDubai.ai — Ask Dubai. Get it done." },
  "/ask": { title: "Ask BuyDubai AI | BuyDubai.ai", desc: "One conversation to discover, compare and act across Dubai. Property is live today." },
  "/vision": { title: "Vision — Investor Demo | BuyDubai.ai", desc: "A live walkthrough of the BuyDubai.ai engine, real traction and the platform architecture.", noindex: true },
  "/explore": { title: "Explore | BuyDubai.ai" },
  "/projects": { title: "Projects | BuyDubai.ai", desc: "Start with your goal, not a category. Turn any Dubai goal into a tracked project." },
  "/property": { title: "Property | BuyDubai.ai", desc: "Property search that starts with a conversation — discovery, pricing intelligence and comparable sales." },
  "/property/": { title: "Property | BuyDubai.ai" },
  "/services": { title: "Services (Coming soon) | BuyDubai.ai" },
  "/experiences": { title: "Experiences (Coming soon) | BuyDubai.ai" },
  "/business": { title: "For Business | BuyDubai.ai" },
  "/restaurants": { title: "Restaurants & Cafes in Dubai | BuyDubai.ai", desc: "Find restaurants, cafes and bakeries across Dubai by name, cuisine and neighbourhood. Listings from OpenStreetMap." },
  "/shopping": { title: "Markets & Shopping in Dubai | BuyDubai.ai", desc: "Supermarkets, grocery and specialty food stores, malls and retail shops across Dubai. Listings from OpenStreetMap." },
  "/directory": { title: "Dubai Business Directory | BuyDubai.ai", desc: "Search local Dubai companies and services by category and neighbourhood. Listings from OpenStreetMap." },
  "/partners": { title: "Partners | BuyDubai.ai", desc: "Turn customer intent into business. Become a founding partner on BuyDubai.ai." },
  "/partners/broker": { title: "Broker CMA (Pilot) | BuyDubai.ai", desc: "For Dubai brokers: price any property in seconds. Run a CMA from recent transaction data, see the comparables used and why, and export a seller-ready report. Pilot — free." },
  "/partners/broker/dashboard": { title: "Broker Dashboard | BuyDubai.ai", desc: "Your saved CMA reports and pilot status in one place." },
  "/admin/pilots": { title: "Admin — Broker Pilots | BuyDubai.ai", desc: "Review and approve broker pilot applications." },
  "/trust": { title: "Trust | BuyDubai.ai" },
  "/about": { title: "About | BuyDubai.ai" },
  "/auth": { title: "Sign in | BuyDubai.ai" },
  "/dashboard": { title: "Dashboard | BuyDubai.ai" },
  "/memory": { title: "What BuyDubai knows about me | BuyDubai.ai" },
  "/privacy": { title: "Privacy | BuyDubai.ai" },
  "/terms": { title: "Terms | BuyDubai.ai" },
};

export const Seo = () => {
  const { pathname } = useLocation();
  const key = META[pathname]
    ? pathname
    : pathname.startsWith("/property/")
    ? "/property/"
    : "/";
  const m = META[key] || META["/"];
  const title = m.title;
  const desc = m.desc || DEFAULT_DESC;
  const url = `${SITE}${pathname}`;
  // Business profiles set their own title/description (BusinessProfile.jsx).
  const isProfile = /^\/(restaurants|shopping|directory)\/[^/]+/.test(pathname);
  return (
    <Helmet>
      {!isProfile && <title>{title}</title>}
      {!isProfile && <meta name="description" content={desc} />}
      <link rel="canonical" href={url} />
      {m.noindex && <meta name="robots" content="noindex, nofollow" />}
      {!isProfile && <meta property="og:title" content={title} />}
      {!isProfile && <meta property="og:description" content={desc} />}
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${SITE}/og-image.jpg`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={`${SITE}/og-image.jpg`} />
    </Helmet>
  );
};
